import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Package, Loader2, Plus, Trash2, Download, ShieldCheck, UserCheck, Send, X, FileText,
} from "lucide-react";
import aapi, { formatApiError, inr, fmtDate } from "./aapi";
import { PageHeader, inputCls, EmptyState } from "@/panel/components";
import { EASE } from "@/components/Motion";

const blankRow = () => ({
  key: Math.random().toString(36).slice(2),
  product: "", ordered_at: "", mrp: "", offer_price: "", paid: "", profit: "",
});

const num = (v) => (v === "" || v === null || isNaN(Number(v)) ? 0 : Number(v));

export default function AdminProductProfit() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [user, setUser] = useState(null);
  const [rows, setRows] = useState([blankRow()]);
  const [note, setNote] = useState("");
  const [emailCustomer, setEmailCustomer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [past, setPast] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => {
      aapi.get("/users", { params: search.trim() ? { search: search.trim() } : {} })
        .then(({ data }) => setUsers(data))
        .catch((e) => toast.error(formatApiError(e)));
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const filtered = useMemo(() => users.slice(0, 25), [users]);

  const loadPast = useCallback(async (uid) => {
    try {
      const { data } = await aapi.get(`/users/${uid}/statement-data`);
      setPast((data.past_statements || []).filter((s) => s.kind === "product_profit"));
    } catch {
      setPast([]);
    }
  }, []);

  const pickUser = (u) => {
    setUser(u);
    setSearch("");
    setResult(null);
    loadPast(u.id);
  };

  const setRow = (key, field, value) =>
    setRows((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r;
        const next = { ...r, [field]: value };
        // Profit auto-computes (paid − offer price) jab tak admin khud profit na badle
        if (field === "paid" || field === "offer_price") {
          next.profit = String(Math.round((num(next.paid) - num(next.offer_price)) * 100) / 100);
        }
        return next;
      })
    );

  const totals = useMemo(() => {
    const t = { mrp: 0, offer: 0, paid: 0, profit: 0 };
    for (const r of rows) {
      t.mrp += num(r.mrp);
      t.offer += num(r.offer_price);
      t.paid += num(r.paid);
      t.profit += num(r.profit);
    }
    t.savings = t.mrp - t.paid;
    return t;
  }, [rows]);

  const validRows = rows.filter((r) => r.product.trim());

  const generate = async () => {
    if (!user) return toast.error("Pehle customer select karein");
    if (!validRows.length) return toast.error("Kam se kam ek product row bharein");
    setBusy(true);
    setResult(null);
    try {
      const payload = {
        rows: validRows.map((r) => ({
          product: r.product.trim(),
          ordered_at: r.ordered_at ? new Date(r.ordered_at).toISOString() : "",
          mrp: num(r.mrp), offer_price: num(r.offer_price), paid: num(r.paid),
          profit: r.profit === "" ? null : num(r.profit),
        })),
        note: note.trim(),
        email_customer: emailCustomer,
      };
      const { data } = await aapi.post(`/users/${user.id}/product-profit`, payload);
      setResult(data);
      toast.success(`Statement ${data.doc_id} ready${data.emailed ? " — email bhi bhej di" : ""}`);
      loadPast(user.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const cellInput = (r, field, placeholder, type = "text", tid) => (
    <input
      type={type}
      value={r[field]}
      onChange={(e) => setRow(r.key, field, e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-2 text-xs text-neutral-100 outline-none transition-colors placeholder:text-neutral-600 focus:border-gold/60"
      data-testid={tid}
    />
  );

  return (
    <div data-testid="admin-product-profit-page">
      <PageHeader
        title="Product Profit Statement"
        sub="Product-wise profit document — MRP, offer price, customer ne kitna pay kiya, aur har product pe profit. Serial auto-sequence, QR verification aur user details bilkul account statement jaisi. Sab kuch aap fill karte hain."
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* customer picker */}
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}
            className="rounded-3xl border border-gold/20 bg-panel p-6" data-testid="pp-customer-card">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-gold/30 bg-gold/10">
                <UserCheck size={16} className="text-gold" />
              </div>
              <div className="font-mono text-[10px] tracking-[0.25em] text-gold/70">CUSTOMER (USER DETAILS STATEMENT PE AAYENGI)</div>
            </div>
            {user ? (
              <div className="mt-4 flex items-center justify-between rounded-xl border border-gold/30 bg-gold/[0.06] px-4 py-3" data-testid="pp-selected-user">
                <div>
                  <p className="text-sm font-semibold text-neutral-100">{user.name}</p>
                  <p className="font-mono text-[10px] text-neutral-500">{user.email} · ID {String(user.id).slice(0, 8).toUpperCase()}</p>
                </div>
                <button onClick={() => { setUser(null); setPast([]); setResult(null); }} data-testid="pp-change-user"
                  className="flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] text-neutral-500 hover:text-goldlight">
                  <X size={12} /> CHANGE
                </button>
              </div>
            ) : (
              <>
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name ya email se customer dhundhein…"
                  className={`${inputCls} mt-4`} data-testid="pp-user-search" />
                <div className="mt-2 max-h-40 overflow-y-auto rounded-xl border border-white/10 bg-white/[0.02] p-1" data-testid="pp-user-list">
                  {filtered.map((u) => (
                    <button key={u.id} onClick={() => pickUser(u)} data-testid={`pp-user-${u.id}`}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-xs text-neutral-300 transition-colors hover:bg-gold/[0.07] hover:text-goldlight">
                      <span className="truncate">{u.name}</span>
                      <span className="ml-auto truncate font-mono text-[10px] text-neutral-600">{u.email}</span>
                    </button>
                  ))}
                  {!filtered.length && <p className="px-3 py-4 text-center text-xs text-neutral-600">Koi user nahi mila</p>}
                </div>
              </>
            )}
          </motion.div>

          {/* rows editor */}
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE, delay: 0.08 }}
            className="rounded-3xl border border-gold/20 bg-panel p-6" data-testid="pp-rows-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-gold/30 bg-gold/10">
                  <Package size={16} className="text-gold" />
                </div>
                <div className="font-mono text-[10px] tracking-[0.25em] text-gold/70">PRODUCT ROWS — S.NO AUTO-SEQUENCE HOGA</div>
              </div>
              <button onClick={() => setRows((p) => [...p, blankRow()])} data-testid="pp-add-row"
                className="flex items-center gap-1.5 rounded-lg border border-gold/40 bg-gold/10 px-3 py-2 font-mono text-[10px] tracking-[0.12em] text-goldlight transition-colors hover:border-gold/70">
                <Plus size={12} /> ADD ROW
              </button>
            </div>

            <div className="mt-4 space-y-3">
              <div className="hidden grid-cols-12 gap-2 px-1 font-mono text-[9px] tracking-[0.12em] text-neutral-600 md:grid">
                <span className="col-span-3">PRODUCT</span>
                <span className="col-span-2">ORDER DATE & TIME</span>
                <span className="col-span-1">MRP ₹</span>
                <span className="col-span-2">OFFER PRICE ₹</span>
                <span className="col-span-2">PAID BY USER ₹</span>
                <span className="col-span-1">PROFIT ₹</span>
                <span className="col-span-1" />
              </div>
              {rows.map((r, i) => (
                <div key={r.key} className="grid grid-cols-2 items-center gap-2 rounded-xl border border-white/5 bg-white/[0.02] p-2 md:grid-cols-12" data-testid={`pp-row-${i}`}>
                  <div className="col-span-2 md:col-span-3">{cellInput(r, "product", `#${i + 1} Product name`, "text", `pp-product-${i}`)}</div>
                  <div className="col-span-2 md:col-span-2">{cellInput(r, "ordered_at", "", "datetime-local", `pp-date-${i}`)}</div>
                  <div className="md:col-span-1">{cellInput(r, "mrp", "MRP", "number", `pp-mrp-${i}`)}</div>
                  <div className="md:col-span-2">{cellInput(r, "offer_price", "Offer price", "number", `pp-offer-${i}`)}</div>
                  <div className="md:col-span-2">{cellInput(r, "paid", "Paid", "number", `pp-paid-${i}`)}</div>
                  <div className="md:col-span-1">{cellInput(r, "profit", "Auto", "number", `pp-profit-${i}`)}</div>
                  <div className="md:col-span-1">
                    <button onClick={() => setRows((p) => p.filter((x) => x.key !== r.key))} disabled={rows.length === 1}
                      data-testid={`pp-remove-${i}`} aria-label="Remove row"
                      className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-neutral-500 transition-colors hover:border-rose-400/40 hover:text-rose-300 disabled:opacity-30">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* totals */}
            <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="pp-totals">
              {[["TOTAL PAID", totals.paid], ["TOTAL MRP", totals.mrp], ["CUSTOMER SAVINGS", totals.savings], ["TOTAL PROFIT", totals.profit]].map(([l, v]) => (
                <div key={l} className="rounded-xl border border-gold/20 bg-gold/[0.05] px-3 py-2.5">
                  <div className="font-mono text-[8px] tracking-[0.15em] text-neutral-500">{l}</div>
                  <div className="mt-1 text-sm font-bold text-goldlight">{inr(v)}</div>
                </div>
              ))}
            </div>

            <div className="mt-5">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Note on statement (optional)</label>
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={400}
                placeholder="e.g. Festive deal pricing — special rates applied for valued customer"
                className={`${inputCls} mt-2 resize-y text-xs`} data-testid="pp-note" />
            </div>

            <label className="mt-4 flex cursor-pointer items-center gap-3 rounded-xl border border-white/10 px-4 py-3" data-testid="pp-email-toggle">
              <input type="checkbox" checked={emailCustomer} onChange={(e) => setEmailCustomer(e.target.checked)}
                className="h-4 w-4 accent-[rgb(var(--gold))]" />
              <span className="flex items-center gap-2 text-xs text-neutral-300">
                <Send size={13} className="text-gold/70" /> PDF customer ko email bhi karein
              </span>
            </label>

            <button onClick={generate} disabled={busy || !user || !validRows.length} data-testid="pp-generate-button"
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3.5 text-sm font-bold tracking-[0.12em] text-black disabled:opacity-40">
              {busy && <Loader2 size={15} className="animate-spin" />}
              GENERATE QR-VERIFIED STATEMENT
            </button>

            {result && (
              <div className="mt-5 rounded-xl border border-emerald-400/30 bg-emerald-400/[0.07] px-5 py-4" data-testid="pp-result">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-200">
                  <ShieldCheck size={16} /> {result.doc_id} issued{result.emailed ? ` · emailed to ${result.emailed_to}` : ""}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a href={result.file_url} target="_blank" rel="noopener noreferrer" data-testid="pp-download"
                    className="flex items-center gap-1.5 rounded-lg bg-gradient-to-br from-goldlight via-gold to-golddark px-4 py-2 font-mono text-[10px] font-bold tracking-[0.12em] text-black">
                    <Download size={12} /> OPEN PDF
                  </a>
                  <a href={result.verify_url} target="_blank" rel="noopener noreferrer" data-testid="pp-verify"
                    className="flex items-center gap-1.5 rounded-lg border border-gold/40 px-4 py-2 font-mono text-[10px] tracking-[0.12em] text-goldlight">
                    <ShieldCheck size={12} /> VERIFY PAGE
                  </a>
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* past documents */}
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE, delay: 0.15 }}
          className="rounded-3xl border border-white/10 bg-panel p-6" data-testid="pp-history">
          <div className="flex items-center gap-3">
            <FileText size={16} className="text-gold" />
            <div className="font-mono text-[10px] tracking-[0.25em] text-gold/70">ISSUED STATEMENTS</div>
          </div>
          <div className="mt-4 space-y-2">
            {!user ? (
              <p className="text-xs text-neutral-600">Customer select karein — uske issued profit statements yahan dikhenge.</p>
            ) : past.length === 0 ? (
              <EmptyState icon={FileText} title="Abhi koi profit statement nahi" sub="Is customer ke liye pehla document generate karein" testid="pp-history-empty" />
            ) : (
              past.map((s) => (
                <div key={s.statement_id} className="rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3" data-testid={`pp-past-${s.statement_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[11px] font-semibold text-goldlight">{s.statement_id}</span>
                    {s.has_file && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/statements/file/${s.statement_id}`} target="_blank" rel="noopener noreferrer"
                        data-testid={`pp-past-open-${s.statement_id}`} aria-label="Open PDF"
                        className="text-neutral-500 transition-colors hover:text-goldlight">
                        <Download size={13} />
                      </a>
                    )}
                  </div>
                  <p className="mt-1 font-mono text-[9px] text-neutral-600">
                    {fmtDate(s.generated_at)} · {s.orders_count} product(s) · Profit {inr(s.closing_balance)}
                  </p>
                </div>
              ))
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
